import React, { useState, useRef, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import DesignSystemDialog from "../dialog-components/DesignSystemDialog";
import { useTranslation } from "react-i18next";
import FeedbackIcon from "@mui/icons-material/Feedback";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	saveUserFeedback,
	uploadFeedbackImage,
	getUserFeedback,
	addFeedbackMessage,
	skipFeedbackConversation
} from "../../database/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import { formatBrandCopy } from "../../utils/brandCopy";
import { getFunctions, httpsCallable } from "firebase/functions";
import FeedbackConversation from "./FeedbackConversation";

// TabPanel component for the tabs
function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`feedback-tabpanel-${index}`}
			aria-labelledby={`feedback-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
		</div>
	);
}


export default function FeedbackDialog({ open, onClose }) {
	const { t, i18n } = useTranslation();
	const { currentUser } = useAuth();
	const [tabValue, setTabValue] = useState(0);
	const [priority, setPriority] = useState(null);
	const [feedback, setFeedback] = useState("");
	const [loading, setLoading] = useState(false);
	const [selectedImages, setSelectedImages] = useState([]);
	const [imagePreviews, setImagePreviews] = useState([]);
	const [userFeedback, setUserFeedback] = useState([]);
	const [loadingFeedback, setLoadingFeedback] = useState(false);
	const fileInputRef = useRef(null);

	// AI Conversation state
	const [conversationMode, setConversationMode] = useState(false);
	const [feedbackId, setFeedbackId] = useState(null);
	const [aiMessages, setAiMessages] = useState([]);
	const [currentQuestion, setCurrentQuestion] = useState(null);
	const [isAiThinking, setIsAiThinking] = useState(false);
	const [conversationComplete, setConversationComplete] = useState(false);
	const [questionCount, setQuestionCount] = useState(0);
	const [savedImageUrls, setSavedImageUrls] = useState([]);
	const MAX_QUESTIONS = 3;
	const AI_TIMEOUT_MS = 15000;

	// Cleanup object URLs when component unmounts
	useEffect(() => {
		return () => {
			imagePreviews.forEach(url => URL.revokeObjectURL(url));
		};
	}, [imagePreviews]);

	// Reset conversation state when dialog closes
	useEffect(() => {
		if (!open) {
			setConversationMode(false);
			setFeedbackId(null);
			setAiMessages([]);
			setCurrentQuestion(null);
			setIsAiThinking(false);
			setConversationComplete(false);
			setQuestionCount(0);
			setSavedImageUrls([]);
		}
	}, [open]);

	const loadUserFeedback = useCallback(async () => {
		if (!currentUser) return;
		setLoadingFeedback(true);
		try {
			const feedbackData = await getUserFeedback(currentUser.uid);
			setUserFeedback(feedbackData);
		} catch (error) {
			console.error("[FeedbackDialog][loadUserFeedback] - Error loading feedback:", error);
			toast.error(t("feedback-dialog-submit-error"));
		} finally {
			setLoadingFeedback(false);
		}
	}, [currentUser, t]);

	// Fetch user feedback when dialog opens and "My Tickets" tab is active
	useEffect(() => {
		if (open && tabValue === 1 && currentUser) {
			loadUserFeedback();
		}
	}, [open, tabValue, currentUser, loadUserFeedback]);

	const handleTabChange = (event, newValue) => {
		// Don't allow tab change during conversation mode
		if (conversationMode && !conversationComplete) {
			return;
		}
		setTabValue(newValue);
		if (newValue === 1 && currentUser) {
			loadUserFeedback();
		}
	};

	const formatDate = (timestamp) => {
		if (!timestamp) return "";
		try {
			const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
			const locale = i18n.language === "ger" ? "de" : i18n.language;
			return date.toLocaleString(locale);
		} catch (error) {
			return "";
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "pending":
				return "warning";
			case "in_progress":
				return "info";
			case "resolved":
				return "success";
			default:
				return "default";
		}
	};

	const getStatusLabel = (status) => {
		switch (status) {
			case "pending":
				return t("feedback-status-pending", "Pending");
			case "in_progress":
				return t("feedback-status-in-progress", "In Progress");
			case "resolved":
				return t("feedback-status-resolved", "Resolved");
			default:
				return t("feedback-status-pending", "Pending");
		}
	};

	const handleImageSelect = (event) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;

		const validImageTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];
		const maxSizeBytes = 50 * 1024 * 1024;

		const newImages = [];
		const newPreviews = [];

		Array.from(files).forEach((file) => {
			if (!validImageTypes.includes(file.type)) {
				toast.error(`${file.name}: ${t("feedback-dialog-image-type-error")}`);
				return;
			}

			if (file.size > maxSizeBytes) {
				toast.error(`${file.name}: ${t("feedback-dialog-image-size-error")}`);
				return;
			}

			newImages.push(file);
			newPreviews.push(URL.createObjectURL(file));
		});

		setSelectedImages((prev) => [...prev, ...newImages]);
		setImagePreviews((prev) => [...prev, ...newPreviews]);

		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleRemoveImage = (index) => {
		setSelectedImages((prev) => prev.filter((_, i) => i !== index));
		setImagePreviews((prev) => {
			URL.revokeObjectURL(prev[index]);
			return prev.filter((_, i) => i !== index);
		});
	};

	// Request AI clarification question
	const requestAIClarification = async (docId, messages = []) => {
		try {
			const functions = getFunctions(undefined, "europe-west3");
			const generateClarification = httpsCallable(functions, "generateFeedbackClarification");

			const timeoutPromise = new Promise((_, reject) =>
				setTimeout(() => reject(new Error("AI_TIMEOUT")), AI_TIMEOUT_MS)
			);

			const result = await Promise.race([
				generateClarification({
					feedbackId: docId,
					initialFeedback: feedback.trim() || "",
					rating: priority,
					hasImages: savedImageUrls.length > 0 || selectedImages.length > 0,
					language: i18n.language,
					conversationHistory: messages
				}),
				timeoutPromise
			]);

			return result.data;
		} catch (error) {
			console.error("[FeedbackDialog][requestAIClarification] - Error:", error);
			throw error;
		}
	};

	// Finalize feedback with AI summary
	const finalizeWithAI = async (docId, messages) => {
		try {
			const functions = getFunctions(undefined, "europe-west3");
			const finalize = httpsCallable(functions, "finalizeFeedbackEnrichment");

			await finalize({
				feedbackId: docId,
				initialFeedback: feedback.trim() || "",
				rating: priority,
				hasImages: savedImageUrls.length > 0,
				language: i18n.language,
				conversationHistory: messages
			});
		} catch (error) {
			console.error("[FeedbackDialog][finalizeWithAI] - Error:", error);
			// Don't throw - feedback is already saved
		}
	};

	const handleSubmit = async () => {
		if (!currentUser) {
			toast.error(t("feedback-dialog-submit-error"));
			return;
		}

		setLoading(true);
		try {
			const imageUrls = [];

			// Upload images if any are selected
			if (selectedImages.length > 0) {
				try {
					const uploadPromises = selectedImages.map(image =>
						uploadFeedbackImage(currentUser.uid, image)
					);
					const urls = await Promise.all(uploadPromises);
					imageUrls.push(...urls);
				} catch (error) {
					console.error("[FeedbackDialog][handleSubmit] - Error uploading images:", error);
					toast.error(t("feedback-dialog-image-upload-error"));
					setLoading(false);
					return;
				}
			}

			// Save feedback with image URLs
			const docId = await saveUserFeedback(currentUser.uid, {
				rating: priority,
				feedback: feedback.trim() || null,
				imageUrls,
				imageUrl: imageUrls.length > 0 ? imageUrls[0] : null,
				feedbackVersion: 2,
			});

			// Store state for conversation
			setFeedbackId(docId);
			setSavedImageUrls(imageUrls);

			// Transition to conversation mode
			setConversationMode(true);
			setIsAiThinking(true);
			setLoading(false);

			// Request first AI question
			try {
				const aiResponse = await requestAIClarification(docId, []);

				if (aiResponse.done) {
					// AI determined no questions needed
					setConversationComplete(true);
					await finalizeWithAI(docId, []);
					toast.success(t("feedback-dialog-submit-success"));
					setTimeout(() => {
						loadUserFeedback();
						setTabValue(1);
					}, 2000);
				} else {
					// Add AI question to messages
					const aiMessage = {
						role: "ai",
						content: aiResponse.question,
						options: aiResponse.options || []
					};
					setAiMessages([aiMessage]);
					setCurrentQuestion(aiResponse);
					setQuestionCount(1);

					// Save AI message to Firestore
					await addFeedbackMessage(currentUser.uid, docId, aiMessage);
				}
			} catch (error) {
				console.error("[FeedbackDialog][handleSubmit] - AI error:", error);
				// Graceful degradation - feedback is saved, just skip AI
				toast.warning(t("feedback-ai-unavailable", "AI assistant is temporarily unavailable. Your feedback has been saved."));
				setConversationComplete(true);
				await skipFeedbackConversation(currentUser.uid, docId);
				setTimeout(() => {
					loadUserFeedback();
					setTabValue(1);
				}, 2000);
			}
		} catch (error) {
			console.error("[FeedbackDialog][handleSubmit] - Error saving feedback:", error);
			toast.error(t("feedback-dialog-submit-error"));
			setLoading(false);
		} finally {
			setIsAiThinking(false);
		}
	};

	const handleConversationAnswer = async (answer) => {
		if (!currentUser || !feedbackId) return;

		// Add user message
		const userMessage = { role: "user", content: answer };
		const updatedMessages = [...aiMessages, userMessage];
		setAiMessages(updatedMessages);

		// Save user message to Firestore
		try {
			await addFeedbackMessage(currentUser.uid, feedbackId, userMessage);
		} catch (error) {
			console.error("[FeedbackDialog][handleConversationAnswer] - Error saving message:", error);
		}

		// Check if we've reached max questions
		if (questionCount >= MAX_QUESTIONS) {
			await handleEndConversation(updatedMessages);
			return;
		}

		// Request next AI question
		setIsAiThinking(true);
		try {
			const aiResponse = await requestAIClarification(feedbackId, updatedMessages);

			if (aiResponse.done) {
				await handleEndConversation(updatedMessages);
			} else {
				const aiMessage = {
					role: "ai",
					content: aiResponse.question,
					options: aiResponse.options || []
				};
				setAiMessages([...updatedMessages, aiMessage]);
				setCurrentQuestion(aiResponse);
				setQuestionCount(prev => prev + 1);

				await addFeedbackMessage(currentUser.uid, feedbackId, aiMessage);
			}
		} catch (error) {
			console.error("[FeedbackDialog][handleConversationAnswer] - AI error:", error);
			toast.warning(t("feedback-ai-unavailable"));
			await handleEndConversation(updatedMessages);
		} finally {
			setIsAiThinking(false);
		}
	};

	const handleSkipQuestion = async () => {
		// Treat skip as "I don't know" or "Not applicable"
		await handleConversationAnswer(t("feedback-ai-skip-response", "Skip - not applicable"));
	};

	const handleEndConversation = async (messages = aiMessages) => {
		if (!currentUser || !feedbackId) return;

		setIsAiThinking(true);
		setConversationComplete(true);

		try {
			await finalizeWithAI(feedbackId, messages);
			toast.success(t("feedback-dialog-submit-success"));
		} catch (error) {
			console.error("[FeedbackDialog][handleEndConversation] - Error:", error);
			// Feedback is already saved, just mark as skipped
			await skipFeedbackConversation(currentUser.uid, feedbackId);
		} finally {
			setIsAiThinking(false);
			// Switch to My Tickets after a delay
			setTimeout(() => {
				loadUserFeedback();
				setTabValue(1);
			}, 2000);
		}
	};

	const handleCancel = () => {
		// If in conversation mode and not complete, ask for confirmation
		if (conversationMode && !conversationComplete) {
			if (window.confirm(t("feedback-cancel-confirmation", "Are you sure? Your feedback has been saved but the AI analysis will be incomplete."))) {
				skipFeedbackConversation(currentUser.uid, feedbackId).catch(console.error);
			} else {
				return;
			}
		}

		// Reset all state
		setPriority(null);
		setFeedback("");
		setSelectedImages([]);
		imagePreviews.forEach(url => URL.revokeObjectURL(url));
		setImagePreviews([]);
		setTabValue(0);
		setConversationMode(false);
		setFeedbackId(null);
		setAiMessages([]);
		setCurrentQuestion(null);
		setConversationComplete(false);
		setQuestionCount(0);
		setSavedImageUrls([]);

		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
		onClose();
	};

	const getPriorityTranslationKey = (value) => {
		switch (value) {
			case 1: return "feedback-priority-essential";
			case 2: return "feedback-priority-reasonable";
			case 3: return "feedback-priority-unimportant";
			default: return "feedback-priority-essential";
		}
	};

	const renderSubmitForm = () => (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
			{/* Priority Selection */}
			<Box>
				<Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
					{t("feedback-dialog-priority-label", "How important is this to you?")}
				</Typography>
				<ToggleButtonGroup
					value={priority}
					exclusive
					onChange={(e, newValue) => {
						if (newValue !== null) setPriority(newValue);
					}}
					fullWidth
					sx={{
						"& .MuiToggleButton-root": {
							textTransform: "none",
							fontWeight: 500,
							py: 1.5,
							display: "flex",
							gap: 0.5,
							flexDirection: "column",
							alignItems: "center",
						},
						"& .MuiToggleButton-root.Mui-selected": {
							backgroundColor: "var(--primary-main)",
							color: "#fff",
							"&:hover": {
								backgroundColor: "var(--primary-main)",
								opacity: 0.9,
							},
						},
					}}
					aria-label={t("feedback-dialog-priority-label", "How important is this to you?")}
				>
					<ToggleButton value={1} disabled={loading}>
						<ErrorOutlineIcon sx={{ fontSize: 20 }} />
						{t("feedback-priority-essential", "Essential")}
					</ToggleButton>
					<ToggleButton value={2} disabled={loading}>
						<WarningAmberIcon sx={{ fontSize: 20 }} />
						{t("feedback-priority-reasonable", "Important")}
					</ToggleButton>
					<ToggleButton value={3} disabled={loading}>
						<InfoOutlinedIcon sx={{ fontSize: 20 }} />
						{t("feedback-priority-unimportant", "Nice-to-have")}
					</ToggleButton>
				</ToggleButtonGroup>
				<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
					{t("feedback-dialog-priority-help", "Select the priority of your feedback")}
				</Typography>
			</Box>

			{/* Feedback Text Field */}
			<TextField
				fullWidth
				multiline
				rows={4}
				label={t("feedback-dialog-comments-label")}
				placeholder={t("feedback-dialog-comments-placeholder")}
				value={feedback}
				onChange={(e) => setFeedback(e.target.value)}
				variant="outlined"
				disabled={loading}
				required={selectedImages.length === 0}
				helperText={
					priority !== null && !feedback.trim() && selectedImages.length === 0
						? t("feedback-description-required", "Please describe your feedback or add an image")
						: ""
				}
				error={priority !== null && !feedback.trim() && selectedImages.length === 0}
			/>

			{/* Image Upload Section */}
			<Box>
				<Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
					{t("feedback-dialog-image-label")}
				</Typography>

				<Box
					sx={{
						border: "2px dashed",
						borderColor: "divider",
						borderRadius: 2,
						p: 2,
						textAlign: "center",
						cursor: "pointer",
						mb: 2,
						"&:hover": {
							borderColor: "primary.main",
							backgroundColor: "action.hover",
						},
					}}
					onClick={() => fileInputRef.current?.click()}
				>
					<ImageIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
					<Typography variant="body2" sx={{ color: "text.secondary" }}>
						{t("feedback-dialog-image-button")}
					</Typography>
					<input
						ref={fileInputRef}
						type="file"
						multiple
						accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
						style={{ display: "none" }}
						onChange={handleImageSelect}
						disabled={loading}
					/>
				</Box>

				{imagePreviews.length > 0 && (
					<Stack spacing={2}>
						{imagePreviews.map((preview, index) => (
							<Box
								key={index}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 2,
									p: 2,
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 2,
									backgroundColor: "background.paper",
								}}
							>
								<Avatar
									src={preview}
									alt={`Preview ${index + 1}`}
									variant="rounded"
									sx={{ width: 80, height: 80 }}
								/>
								<Box sx={{ flex: 1 }}>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{selectedImages[index]?.name}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{selectedImages[index]
											? `${(selectedImages[index].size / 1024 / 1024).toFixed(2)} MB`
											: ""}
									</Typography>
								</Box>
								<IconButton
									color="error"
									onClick={() => handleRemoveImage(index)}
									disabled={loading}
									size="small"
								>
									<DeleteIcon />
								</IconButton>
							</Box>
						))}
					</Stack>
				)}
			</Box>
		</Box>
	);

	const renderMyTickets = () => (
		<Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
			{loadingFeedback ? (
				<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
					<CircularProgress />
					<Typography sx={{ ml: 2 }}>{t("feedback-loading-tickets", "Loading your feedback...")}</Typography>
				</Box>
			) : userFeedback.length === 0 ? (
				<Box sx={{ textAlign: "center", p: 4 }}>
					<Typography variant="body1" color="text.secondary">
						{t("feedback-no-tickets", "You haven't submitted any feedback yet.")}
					</Typography>
				</Box>
			) : (
				<Stack spacing={2}>
					{userFeedback.map((item, index) => (
						<React.Fragment key={item.id}>
							<Card>
								<CardContent>
									<Stack spacing={2}>
										<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
											<Box>
												<Typography variant="body2" color="text.secondary">
													{t("feedback-submitted-on", "Submitted on")} {formatDate(item.timestamp)}
												</Typography>
											</Box>
											<Chip
												label={getStatusLabel(item.status)}
												color={getStatusColor(item.status)}
												size="small"
											/>
										</Box>
										<Box>
											<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
												{item.feedbackVersion >= 2
													? `${t("feedback-priority-label", "Priority")}: ${t(getPriorityTranslationKey(item.rating))}`
													: `${t("feedback-rating-label", "Rating")}: ${item.rating}/10`
												}
											</Typography>
										</Box>
										{item.feedback && (
											<Box>
												<Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
													{item.feedback}
												</Typography>
											</Box>
										)}
										{(item.imageUrls || item.imageUrl) && (
											<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
												{(item.imageUrls || [item.imageUrl]).map((url, imgIndex) => (
													url && (
														<Avatar
															key={imgIndex}
															src={url}
															alt={`Feedback image ${imgIndex + 1}`}
															variant="rounded"
															sx={{ width: 100, height: 100, cursor: 'pointer' }}
															onClick={() => window.open(url, '_blank')}
															onError={(e) => {
																e.target.style.display = "none";
															}}
														/>
													)
												))}
											</Box>
										)}

										{(item.status === "in_progress" || item.status === "resolved") && item.adminNotes && (
											<Box
												sx={{
													mt: 1,
													p: 2,
													backgroundColor: "action.hover",
													borderRadius: 1,
												}}
											>
												<Typography variant="caption" sx={{ fontWeight: 500, display: "block", mb: 0.5 }}>
													{t("feedback-admin-notes-label", "Admin Notes")}:
												</Typography>
												<Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
													{item.adminNotes}
												</Typography>
											</Box>
										)}
									</Stack>
								</CardContent>
							</Card>
							{index < userFeedback.length - 1 && <Divider />}
						</React.Fragment>
					))}
				</Stack>
			)}
		</Box>
	);

	return (
		<DesignSystemDialog
			open={open}
			onClose={handleCancel}
			title={conversationMode ? t("feedback-conversation-title", "Help us understand better") : formatBrandCopy(t("feedback-dialog-title"))}
			icon={conversationMode ? <SmartToyIcon /> : <FeedbackIcon />}
			maxWidth="sm"
			actions={
				conversationMode ? (
					conversationComplete ? (
						<Button
							variant="contained"
							onClick={handleCancel}
							sx={{
								borderRadius: "12px",
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							{t("feedback-dialog-close", "Close")}
						</Button>
					) : null
				) : (
					<>
						<Button
							variant="outlined"
							onClick={handleCancel}
							disabled={loading}
							sx={{
								borderRadius: "12px",
								textTransform: "none",
								fontWeight: 600,
								transition: "all 0.2s ease",
							}}
						>
							{t("feedback-dialog-cancel")}
						</Button>
						{tabValue === 0 && (
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmit}
								disabled={loading || priority === null || (!feedback.trim() && selectedImages.length === 0)}
								sx={{
									borderRadius: "12px",
									textTransform: "none",
									fontWeight: 600,
									transition: "all 0.2s ease",
								}}
							>
								{loading ? (
									<CircularProgress size={20} color="inherit" />
								) : (
									t("feedback-dialog-submit")
								)}
							</Button>
						)}
					</>
				)
			}
		>
			{conversationMode ? (
				// Conversation Mode UI
				<FeedbackConversation
					messages={aiMessages}
					currentQuestion={currentQuestion}
					onAnswer={handleConversationAnswer}
					onSkip={handleSkipQuestion}
					onEnd={() => handleEndConversation()}
					isThinking={isAiThinking}
					isComplete={conversationComplete}
					questionCount={questionCount}
					maxQuestions={MAX_QUESTIONS}
				/>
			) : (
				// Normal Form UI
				<>
					<Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
						<Tabs value={tabValue} onChange={handleTabChange} centered>
							<Tab label={t("feedback-tab-submit", "Submit Feedback")} />
							<Tab label={t("feedback-tab-my-tickets", "My Tickets")} />
						</Tabs>
					</Box>

					<TabPanel value={tabValue} index={0}>
						{renderSubmitForm()}
					</TabPanel>

					<TabPanel value={tabValue} index={1}>
						{renderMyTickets()}
					</TabPanel>
				</>
			)}
		</DesignSystemDialog>
	);
}
